import { BookingEntity } from 'Models/Entities';
import {
	EventBookingWizardData,
	SelectedTicketsDto,
} from 'Views/Components/_HumanWritten/BookingWizard/EventsBookingWizard/EventsBookingWizardData';
import { getMostRecentBookedOrReservedAlteration } from '../AlterationSortingUtils';
import { isNullOrUndefined } from 'Util/TypeGuards';

// Converts a bookingEntity to an instance of BookingWizardData to be used in the local storage
// primarily for editing bookings from the cart tab
export function eventBookingToWizardData(booking: BookingEntity): EventBookingWizardData {
	const mostRecentAlteration = getMostRecentBookedOrReservedAlteration(booking.alterations);
	return {
		acceptedTsAndCs: false,
		eventId: mostRecentAlteration.ferryTrip.eventDetailId,
		seriesId: mostRecentAlteration.ferryTrip.eventDetail?.eventSeriesId ?? '',
		userId: booking.userId,
		firstName: booking.user.firstName,
		lastName: booking.user.lastName,
		email: booking.user.email,
		phone: booking.user.phone,
		selectedTickets: mostRecentAlteration.tickets.map(ticket => {
			return {
				customTicketTypeId: ticket.customTicketTypeId,
				customTicketType: ticket.customTicketType,
				quantity: ticket.amount,
			} as SelectedTicketsDto;
		}),
		bookingToEditId: booking.id,
		wizardMode: 'ALTERATION',
	} as EventBookingWizardData;
}

/**
 * Converts an existing event booking to EventBookingWizardData for editing.
 * Uses bookedSummary data which contains the primary contact info for the booking.
 * This is the preferred method for edit flows from BookingDetailedView.
 */
export function convertEventBookingToWizardData(booking: BookingEntity): EventBookingWizardData {
	const { bookedSummary } = booking;

	if (isNullOrUndefined(bookedSummary)) {
		// Fallback to alteration-based conversion if bookedSummary is not available
		return eventBookingToWizardData(booking);
	}

	const eventDetail = bookedSummary.ferryTrip?.eventDetail;

	// Convert selected tickets from booking summary
	const selectedTickets: SelectedTicketsDto[] = (bookedSummary.selectedTickets ?? [])
		.filter(ticket => ticket.quantity > 0)
		.map(ticket => ({
			customTicketTypeId: ticket.customTicketTypeId,
			quantity: ticket.quantity,
		}));

	return {
		acceptedTsAndCs: false, // User must re-accept during edit
		eventId: eventDetail?.id ?? bookedSummary.eventId ?? '',
		seriesId: eventDetail?.eventSeriesId ?? '',
		userId: booking.userId ?? '',
		firstName: bookedSummary.primaryFirstName ?? '',
		lastName: bookedSummary.primaryLastName ?? '',
		email: bookedSummary.primaryEmail ?? '',
		phone: bookedSummary.primaryPhone ?? '',
		selectedTickets: selectedTickets,
		bookingToEditId: booking.id,
		wizardMode: 'ALTERATION',
	};
}
